import theme from '@/presentation/assets/styles/theme'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import { IconButton, InputAdornment, TextField, TextFieldProps, styled } from '@mui/material'
import { KeyboardEvent, useEffect, useState } from 'react'

export type SearchFieldOnChangeEvent =
  | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  | string
export type SearchFieldOnChangeFunc = (e?: SearchFieldOnChangeEvent) => void

type SearchFieldProps = TextFieldProps & {
  onChange?: SearchFieldOnChangeFunc
  onSearch?: (value: string | undefined) => void
  onBlurChange?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  onFocusChange?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  onFieldChange?: (value: string | undefined) => void
  onClearInput?: () => void
}

const StyledField = styled(TextField)<SearchFieldProps>(() => ({
  '& .MuiInputBase-root': {
    borderRadius: 999,
    height: theme.spacing(6),
    backgroundColor: theme.palette.common.white
  }
}))

export default function SearchField(props: SearchFieldProps) {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    value,
    onChange,
    onSearch,
    onBlurChange,
    onFocusChange,
    onFieldChange,
    onClearInput,
    ...rest
  } = props
  const [innerValue, setInnerValue] = useState<string | undefined>(value as string | undefined)

  useEffect(() => {
    if (value !== undefined) {
      setInnerValue(value as string)
      setShowClearButton(!!value)
    }
  }, [value])

  const [showClearButton, setShowClearButton] = useState<boolean>(!!props.value)

  const innerOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.target.value
    setShowClearButton(!!value)
    setInnerValue(value)
    onFieldChange?.(value)
  }

  const handleClear = () => {
    setInnerValue('')
    setShowClearButton(false)
    onClearInput?.()
  }

  const handleSearch = () => {
    onSearch?.(innerValue)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const ClearButton = (
    <IconButton onClick={handleClear}>
      <CancelIcon />
    </IconButton>
  )
  return (
    <StyledField
      fullWidth
      placeholder="O que você deseja aprender?"
      onKeyDown={handleKeyDown}
      onBlur={onBlurChange}
      onFocus={onFocusChange}
      InputProps={{
        sx: { px: theme => theme.spacing(1), color: theme.palette.grey[500] },
        endAdornment: (
          <InputAdornment position="end">
            {showClearButton && ClearButton}
            <IconButton
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme.palette.primary[400],
                  color: theme.palette.common.white
                }
              }}
              onClick={handleSearch}
            >
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        )
      }}
      onChange={innerOnChange as SearchFieldOnChangeFunc}
      {...rest}
      value={innerValue}
    />
  )
}
